import { FAINT, INK, MALL_ROOMS, OUTLINE, WALL, HALL, readableOn, wrapName } from "@/vault/directory/map/floorplan";

const DISPLAY = "'Cabinet Grotesk', 'Inter', sans-serif";
const MONO = "'JetBrains Mono', monospace";
const AMBER = "#52525B";

function Slot({ slot, on, onHover, onPick, highlight }) {
    const { store, unit, kind } = slot;
    const lit = highlight && store && store.slug === highlight.slug;
    const color = lit ? highlight.color : slot.color;
    const cx = slot.x + slot.w / 2;
    const cy = slot.y + slot.h / 2;
    const anchor = kind !== "room" && slot.tier === "department";
    const cart = slot.tier === "kiosk";
    const plaza = slot.room === "plaza";
    const type = readableOn(color);
    const sub = type === "#ffffff" ? "rgba(255,255,255,0.75)" : FAINT;
    const shape = slot.points
        ? <polygon points={slot.points} fill={color} stroke={on || lit ? "#0B0B0B" : OUTLINE} strokeWidth={on || lit ? 4 : 1.6} />
        : <rect x={slot.x} y={slot.y} width={slot.w} height={slot.h} fill={color}
            stroke={on || lit ? "#0B0B0B" : OUTLINE} strokeWidth={on || lit ? 4 : 1.6} />;

    return (
        <g data-testid={kind === "store" ? `map-slot-${store.slug}` : kind === "room" ? `map-room-${slot.room}` : `map-unit-${unit}`}
            data-lit={lit || undefined}
            onMouseEnter={() => onHover(slot)} onMouseLeave={() => onHover(null)} onClick={() => onPick(slot)}
            style={{
                cursor: "pointer",
                transform: on ? "translate(-3px, -6px)" : "translate(0,0)",
                transition: "transform 150ms ease-out",
                filter: on ? "url(#mm-lift)" : cart ? "url(#mm-cart)" : "none",
            }}>
            {shape}

            {plaza && (
                <>
                    <text x={cx} y={slot.y + 76} textAnchor="middle" fontFamily={DISPLAY} fontSize={30}
                        fontWeight="800" letterSpacing="-0.02em" fill={type}>{MALL_ROOMS.plaza.name.toUpperCase()}</text>
                    <text x={cx} y={slot.y + 98} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={sub} letterSpacing="0.26em">{unit} · {MALL_ROOMS.plaza.sub.toUpperCase()}</text>
                    <text x={cx} y={slot.y + 128} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={sub} letterSpacing="0.2em">OPEN TO EVERY SHOP</text>
                </>
            )}

            {kind === "room" && !plaza && (
                <>
                    {wrapName(MALL_ROOMS[slot.room].name, 11, 2).map((line, i, all) => (
                        <text key={line} x={cx} y={cy - 2 + i * 19 - (all.length - 1) * 8} textAnchor="middle"
                            fontFamily={DISPLAY} fontSize={17} fontWeight="800" letterSpacing="-0.01em" fill={type}>
                            {line}
                        </text>
                    ))}
                    <text x={cx} y={slot.y + slot.h - 12} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={sub} letterSpacing="0.2em">
                        {unit} · {MALL_ROOMS[slot.room].sub.toUpperCase()}
                    </text>
                </>
            )}

            {kind === "store" && anchor && (
                <>
                    {wrapName(store.name, 13, 3).map((line, i, all) => (
                        <text key={line + i} x={cx} y={cy - 6 + i * 30 - (all.length - 1) * 11} textAnchor="middle"
                            fontFamily={DISPLAY} fontSize={26} fontWeight="800" letterSpacing="-0.02em" fill={type}>
                            {line}
                        </text>
                    ))}
                    <text x={cx} y={slot.y + slot.h - 22} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={sub} letterSpacing="0.2em">{(store.city || "").toUpperCase()}</text>
                    <text x={slot.numAt ? slot.numAt[0] : slot.x + 14} y={slot.numAt ? slot.numAt[1] : slot.y + 26}
                        fontFamily={MONO} fontSize={14} fill={type}>{unit}</text>
                </>
            )}

            {kind === "store" && !anchor && !cart && (
                <>
                    {wrapName(store.name, 12, 3).map((line, i, all) => (
                        <text key={line + i} x={cx} y={cy + 4 + i * 17 - (all.length - 1) * 8} textAnchor="middle"
                            fontFamily={DISPLAY} fontSize={15} fontWeight="700" letterSpacing="-0.01em" fill={type}>
                            {line}
                        </text>
                    ))}
                    <text x={slot.x + 8} y={slot.y + 18} fontFamily={MONO} fontSize={11} fill={sub}>{unit}</text>
                </>
            )}

            {kind === "store" && cart && (
                <text x={cx} y={cy + 6} textAnchor="middle" fontFamily={MONO} fontSize={16} fontWeight="700"
                    fill={type}>{unit}</text>
            )}

            {kind === "vacant" && (
                <text x={cx} y={on ? cy - 2 : cy + 6} textAnchor="middle" fontFamily={MONO}
                    fontSize={anchor ? 20 : 15} fill={on ? type : sub}>{unit}</text>
            )}
            {kind === "vacant" && on && (
                <text x={cx} y={cy + 16} textAnchor="middle" fontFamily={MONO} fontSize={11}
                    fill={type} letterSpacing="0.18em">OPEN</text>
            )}
        </g>
    );
}

/** The pad sites — same property, own building. One is for lease; one is 13thav's own shop. */
function PadSite({ pad, on, onHover, onPick, highlight }) {
    const cx = pad.x + pad.w / 2;
    const taken = pad.store || null;
    const lit = highlight && taken && taken.slug === highlight.slug;
    const padFill = lit ? highlight.color : taken ? "#FFFFFF" : "#F4F4F5";
    const padInk = lit ? readableOn(highlight.color) : INK;
    return (
        <g data-testid={taken ? `map-pad-${taken.slug}` : "map-pad-site"} data-lit={lit || undefined}
            onMouseEnter={() => onHover(pad)} onMouseLeave={() => onHover(null)}
            onClick={() => onPick(pad)}
            style={{
                cursor: "pointer",
                transform: on ? "translate(-3px, -6px)" : "translate(0,0)",
                transition: "transform 150ms ease-out",
                filter: on ? "url(#mm-lift)" : "none",
            }}>
            <rect x={pad.x - 26} y={pad.y - 20} width={pad.w + 52} height={pad.h + 66} fill={HALL}
                stroke={WALL} strokeWidth={2} strokeDasharray="10 8" />
            <text x={cx} y={pad.y + pad.h + 34} textAnchor="middle" fontFamily={MONO} fontSize={11}
                fill={FAINT} letterSpacing="0.24em">
                {taken ? "PRIVATE SITE · OWN DOMAIN" : "PRIVATE SITE · OPEN"}
            </text>
            <polygon points={pad.points} fill={padFill}
                stroke={on || lit ? "#0B0B0B" : AMBER} strokeWidth={on || lit ? 4 : 2.4}
                strokeDasharray={on || taken ? "0" : "12 7"} />

            {taken ? (
                <>
                    {wrapName(taken.name, 13, 2).map((line, i, all) => (
                        <text key={line + i} x={cx} y={pad.y + 60 + i * 30 - (all.length - 1) * 12}
                            textAnchor="middle" fontFamily={DISPLAY} fontSize={26} fontWeight="800"
                            letterSpacing="-0.02em" fill={padInk}>{line}</text>
                    ))}
                    <text x={cx} y={pad.y + 108} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={lit ? padInk : AMBER} letterSpacing="0.2em">VIRTUAL BRICK &amp; MORTAR</text>
                    <text x={cx} y={pad.y + 138} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={FAINT} letterSpacing="0.16em">
                        {on ? "ORDER ONLINE · OWN SITE"
                            : `${(taken.city || "").toUpperCase()} · OPEN`}
                    </text>
                </>
            ) : (
                <>
                    <text x={cx} y={pad.y + 52} textAnchor="middle" fontFamily={DISPLAY} fontSize={26} fontWeight="800"
                        letterSpacing="-0.02em" fill={INK}>VIRTUAL</text>
                    <text x={cx} y={pad.y + 80} textAnchor="middle" fontFamily={DISPLAY} fontSize={26} fontWeight="800"
                        letterSpacing="-0.02em" fill={INK}>BRICK &amp; MORTAR</text>
                    <text x={cx} y={pad.y + 108} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={AMBER} letterSpacing="0.22em">YOUR OWN PRIVATE WEBSITE</text>
                    <text x={cx} y={pad.y + 138} textAnchor="middle" fontFamily={MONO} fontSize={11}
                        fill={FAINT} letterSpacing="0.16em">
                        {on ? "PRODUCTS · DESIGNERS · YOUR OWN WEB ADDRESS" : "NO DIRECTORY · JUST YOU"}</text>
                </>
            )}
            <text x={pad.x + 12} y={pad.y + 22} fontFamily={MONO} fontSize={12} fill={FAINT}>{pad.unit}</text>
        </g>
    );
}

/** The board you walk up to. Point at a room and it lifts; tap it and you go there. */
export default function MallMap({ plan, hoverId, onHover, onPick, highlight = null, compact = false }) {
    const { view } = plan;
    const pads = plan.pads || [plan.pad];
    const hot = [...plan.slots, ...pads].find((s) => s && s.id === hoverId);
    const hotLabel = hot && (hot.store?.name
        || (hot.kind === "room" ? MALL_ROOMS[hot.room].name
            : hot.kind === "pad" ? "Your own private site" : "Open — this space is yours"));
    // the plaza is the floor of the court — painted before anything standing on it
    const drawn = [...plan.slots].sort((a, b) => (a.kind === "room" ? 0 : 1) - (b.kind === "room" ? 0 : 1));

    return (
        <div className="flex h-full max-h-full w-full flex-col overflow-hidden border border-zinc-300 bg-white"
            data-testid="mall-map">
            <div className="dark-block flex shrink-0 items-center justify-between px-4 py-2.5">
                <p className="font-display text-xs font-extrabold uppercase tracking-[0.2em] text-white sm:text-sm">
                    Directory map
                </p>
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-400">{highlight ? "you are in the highlighted shop" : "click a shop to open it"}</p>
            </div>
            <div className="h-[3px] shrink-0 bg-zinc-900" />

            <div className="relative min-h-0 flex-1 p-2.5 sm:p-4">
                <svg viewBox={`0 0 ${view.w} ${view.h}`} preserveAspectRatio="xMidYMid meet"
                    className={`${compact ? "h-full" : "max-h-[70vh]"} w-full select-none`} role="img"
                    aria-label="The 13th Ave Mall floor plan">
                    <defs>
                        <filter id="mm-lift" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="6" stdDeviation="5" floodColor="#000" floodOpacity="0.3" />
                        </filter>
                        <filter id="mm-cart" x="-40%" y="-40%" width="180%" height="180%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.22" />
                        </filter>
                    </defs>

                    {/* the building: heavy outer wall, then the concourse floor */}
                    <polygon points={plan.shell} fill="none" stroke={WALL} strokeWidth={18} strokeLinejoin="round" />
                    <polygon points={plan.shell} fill={HALL} stroke={OUTLINE} strokeWidth={2.4} strokeLinejoin="round" />

                    {/* new wings, built as the directory fills up */}
                    {(plan.wings || []).map((w) => (
                        <g key={w.points} data-testid={`map-wing-${w.label.replace(/\s+/g, "-").toLowerCase()}`}>
                            <polygon points={w.points} fill="none" stroke={WALL} strokeWidth={18} strokeLinejoin="round" />
                            <polygon points={w.points} fill={HALL} stroke={OUTLINE} strokeWidth={2.4} strokeLinejoin="round" />
                            <text x={w.at[0]} y={w.at[1]} textAnchor="middle" fontFamily={MONO} fontSize={11}
                                fill={FAINT} letterSpacing="0.24em">{w.label.toUpperCase()} · NEW</text>
                        </g>
                    ))}

                    {plan.entrances.map((e) => (
                        <g key={e.key}>
                            <rect x={e.x} y={e.y} width={e.w} height={e.h} fill="#18181B" stroke={OUTLINE} strokeWidth={1.2} />
                            <text x={e.labelAt[0]} y={e.labelAt[1]} textAnchor={e.anchor} fontFamily={MONO} fontSize={11}
                                fill={FAINT} letterSpacing="0.2em">ENTRANCE {e.key}</text>
                        </g>
                    ))}

                    {drawn.map((s) => (
                        <Slot key={s.id} slot={s} on={hoverId === s.id} onHover={onHover} onPick={onPick} highlight={highlight} />
                    ))}

                    <PadSite pad={pads[0]} on={hoverId === pads[0].id} onHover={onHover} onPick={onPick} highlight={highlight} />
                    {pads.slice(1).map((p) => (
                        <PadSite key={p.id} pad={p} on={hoverId === p.id} onHover={onHover} onPick={onPick} highlight={highlight} />
                    ))}

                    <g data-testid="map-you-are-here">
                        <circle cx={plan.here.x} cy={plan.here.y} r={7} fill="#B3261E" stroke="#ffffff" strokeWidth={2.5} />
                        <text x={plan.here.x + 14} y={plan.here.y + 4} fontFamily={MONO} fontSize={11}
                            fill="#B3261E" letterSpacing="0.14em">YOU ARE HERE</text>
                    </g>

                    <g transform={`translate(${view.w - 70}, ${view.h - 84})`} data-testid="map-north">
                        <path d="M0 26 L8 0 L16 26 L8 19.5 Z" fill={FAINT} />
                        <text x="8" y="42" textAnchor="middle" fontFamily={MONO} fontSize={11} fill={FAINT}>N</text>
                    </g>
                </svg>

                {hot && (
                    <div className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full pb-2"
                        style={{
                            left: `calc(${((hot.x + hot.w / 2) / view.w) * 100}% )`,
                            top: `${(hot.y / view.h) * 100}%`,
                        }}
                        data-testid="map-tooltip">
                        <span className="dark-block block whitespace-nowrap rounded px-2.5 py-1.5 font-display text-[11px] font-bold text-white shadow-[0_8px_22px_-10px_rgba(0,0,0,0.6)]">
                            <span className="font-mono text-[11px] text-zinc-400">{hot.unit}</span> {hotLabel}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
